import { useState, useEffect, useCallback, useRef } from "react";
import { FetchLocationsResult, LocationsResponse } from "../../../types/seedGo";
import { fetchApi } from "../../../config/api";

const useFetchLocations = (
  initialLat: number,
  initialLon: number,
  initialZoom: 0 | 1 | 2 | 3,
): FetchLocationsResult => {
  const [data, setData] = useState<LocationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [lat, setLat] = useState<number>(initialLat);
  const [lon, setLon] = useState<number>(initialLon);
  const [zoom, setZoom] = useState<0 | 1 | 2 | 3>(initialZoom);

  const isMounted = useRef(false);

  const fetchData = useCallback(
    async (fetchLat: number, fetchLon: number, fetchZoom: 0 | 1 | 2 | 3) => {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchApi(
          "GET",
          `/seedmon/locations?lat=${fetchLat}&lon=${fetchLon}&zoom=${fetchZoom}`,
        );

        if (result.data) {
          const responseData = await result.data.json();
          if (responseData.data && Array.isArray(responseData.data)) {
            setData(responseData.data);
          } else {
            throw new Error("Invalid data format from API.");
          }
        } else if (result.error) {
          throw result.error;
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      if (lat && lon) {
        fetchData(lat, lon, zoom);
      }
    }
  }, [fetchData, lat, lon, zoom]);

  const refetch = useCallback(
    (newLat: number, newLon: number, newZoom: 0 | 1 | 2 | 3) => {
      setLat(newLat);
      setLon(newLon);
      setZoom(newZoom);
      fetchData(newLat, newLon, newZoom);
    },
    [fetchData],
  );

  return { data, error, loading, refetch };
};

export default useFetchLocations;
